import { delay, http, HttpResponse } from "msw";
import { apiPaths } from "../../api-paths.js";
import type { Trips_CreateTrip_CreateTripFailedReason } from "../../swagger-types/v1/index.js";
import { DateTime } from "luxon";
import { dateTimeToDateOnly } from "@holisto-web/date-utils";
import { getFallbackImageUrl } from "../image-fixtures.js";

type HandlerDelay = number | "infinite";

const waitForResponseDelay = async (delayMs?: HandlerDelay) => {
  if (delayMs === "infinite") {
    await delay("infinite");
    return;
  }

  if (typeof delayMs === "number" && delayMs > 0) {
    await delay(delayMs);
  }
};

const createTrip200Handler = [
  http.post(`**${apiPaths.trips.create}`, async () => {
    return HttpResponse.json(
      {
        tripKey: "86df5ee0-e9b4-4805-986c-f1656e1a4ccd",
        status: "ProcessingAtPaymentProvider",
        redirectUrl: null,
      },
      { status: 200 },
    );
  }),
];

const createTrip200WithRedirectHandler = (redirectUrl: string) => [
  http.post(`**${apiPaths.trips.create}`, async () => {
    return HttpResponse.json(
      {
        tripKey: "TRIPKEY",
        status: "ProcessingAtPaymentProvider",
        redirectUrl,
      },
      { status: 200 },
    );
  }),
];

const createTripDuplicateHandler = [
  http.post(`**${apiPaths.trips.create}`, async () => {
    return HttpResponse.json({}, { status: 409 });
  }),
];

const createTripFailedHandler = (
  failedReason: Trips_CreateTrip_CreateTripFailedReason,
  tripKey: string | null = "TRIPKEY",
) => [
  http.post(`**${apiPaths.trips.create}`, async () => {
    return HttpResponse.json(
      {
        failedReason,
        tripKey,
        status: null,
        invalidCreditCodes: null,
      },
      { status: 422 },
    );
  }),
];

const getTripKeyMock = { hotelId: 12345, tripKey: "471ea13e-58bb-49d1-a810-9cb1efb22e1b" } as const;

interface GetTripKeyHandlerOptions {
  status?: number;
  body?: Record<string, unknown>;
  delayMs?: HandlerDelay;
  onRequest?: (request: Request) => void | Promise<void>;
}

const createGetTripKeyHandler = ({
  status = 200,
  body = getTripKeyMock,
  delayMs,
  onRequest,
}: GetTripKeyHandlerOptions = {}) => [
  http.post(`**${apiPaths.trips.getTripKey}`, async ({ request }) => {
    await onRequest?.(request);
    await waitForResponseDelay(delayMs);
    return HttpResponse.json(body, { status });
  }),
];

const getTripKeyHandler = createGetTripKeyHandler();
const getTripKey401Handler = createGetTripKeyHandler({
  status: 401,
  body: { error: "Invalid credentials" },
  delayMs: 300,
});
const getTripKeyLoadingHandler = createGetTripKeyHandler({ delayMs: "infinite" });

const createTripReviewLinkHandler = (
  reviewUrl = "http://some.domain/some/path/to/review",
  status = 200,
  onRequest?: (request: Request) => void,
) => [
  http.get(`**${apiPaths.trips.getReviewLink}`, ({ request }) => {
    onRequest?.(request);
    return HttpResponse.json(status >= 400 ? {} : { reviewUrl }, { status });
  }),
];

const getTripReviewLinkLoadingHandler = [
  http.post(`**${apiPaths.trips.getReviewLink}`, async () => {
    await delay("infinite");
    return HttpResponse.json({ reviewUrl: "http://some.domain/some/path/to/review" }, { status: 200 });
  }),
];

const createCancelTripHandler = (status = 200, delayMs?: HandlerDelay) => [
  http.delete(`**${apiPaths.trips.cancel}*`, async () => {
    await waitForResponseDelay(delayMs);
    return HttpResponse.json({}, { status });
  }),
];

const cancelTrip200Handler = createCancelTripHandler();

const createSendReceiptHandler = (status = 200) => [
  http.post(`**${apiPaths.trips.sendReceipt.replace(":tripKey", "*")}`, async () => {
    return HttpResponse.json({}, { status });
  }),
];

const createTripEmailActionHandler = (path: string, status: number) => [
  http.post(`**${path}`, () => HttpResponse.json({}, { status })),
];

const sendReceipt200Handler = createTripEmailActionHandler(apiPaths.trips.sendReceipt, 200);
const sendReceipt500Handler = createTripEmailActionHandler(apiPaths.trips.sendReceipt, 500);
const sendItinerary200Handler = createTripEmailActionHandler(apiPaths.trips.sendItinerary, 200);

const createMyTrips = (now: DateTime) => {
  return {
    trips: [
      {
        tripId: 47045,
        tripKey: "euY2JiDvbKh8mt8RbL8lxw==",
        status: "Confirmed",
        hotel: {
          id: 125866,
          name: "Andaz 5th Avenue",
          info: {
            checkinTime: "4:00 PM-midnight",
            checkoutTime: "11:00",
          },
          mainImage: {
            largeUrl: getFallbackImageUrl(),
          },
          location: {
            countryName: "United States of America",
            stateCode: "NY",
            cityName: "Long Island City",
            address: "485 5th Avenue at 41st Street, New York, New York 10017",
            latitude: 40.75253,
            longitude: -73.981316,
          },
        },
        checkIn: dateTimeToDateOnly(now),
        checkOut: dateTimeToDateOnly(now.plus({ day: 1 })),
        hotelConfirmationNumber: null,
      },
      {
        tripId: 47045,
        tripKey: "euY2JiDvbKh8mt8RbL9lxw==",
        status: "Confirmed",
        hotel: {
          id: 125866,
          name: "Andaz 5th Avenue",
          info: {
            checkinTime: "4:00 PM-midnight",
            checkoutTime: "11:00",
          },
          mainImage: {
            largeUrl: getFallbackImageUrl(),
          },
          location: {
            countryName: "United States of America",
            stateCode: "NY",
            cityName: "Long Island City",
            address: "485 5th Avenue at 41st Street, New York, New York 10017",
            latitude: 40.75253,
            longitude: -73.981316,
          },
        },
        checkIn: dateTimeToDateOnly(now.minus({ day: 1 })),
        checkOut: dateTimeToDateOnly(now.plus({ day: 1 })),
        hotelConfirmationNumber: null,
      },
      {
        tripId: 47042,
        tripKey: "euY2JiDvbKh8rmt8RbL9lxw==",
        status: "Confirmed",
        hotel: {
          id: 125866,
          name: "Andaz 5th Avenue",
          info: {
            checkinTime: "4:00 PM-midnight",
            checkoutTime: "11:00",
          },
          mainImage: {
            largeUrl: getFallbackImageUrl(),
          },
          location: {
            countryName: "United States of America",
            stateCode: "NY",
            cityName: "Long Island City",
            address: "485 5th Avenue at 41st Street, New York, New York 10017",
            latitude: 40.75253,
            longitude: -73.981316,
          },
        },
        checkIn: dateTimeToDateOnly(now.minus({ day: 2 })),
        checkOut: dateTimeToDateOnly(now.plus({ day: 1 })),
        hotelConfirmationNumber: null,
      },
      {
        tripId: 47046,
        tripKey: "euY2JiDvbKh8mt8RbL8lxw==",
        status: "Confirmed",
        hotel: {
          id: 125866,
          name: "Andaz 5th Avenue",
          info: {
            checkinTime: "4:00 PM-midnight",
            checkoutTime: "11:00",
          },
          mainImage: {
            largeUrl: getFallbackImageUrl(),
          },
          location: {
            countryName: "United States of America",
            stateCode: "NY",
            cityName: "Long Island City",
            address: "485 5th Avenue at 41st Street, New York, New York 10017",
            latitude: 40.75253,
            longitude: -73.981316,
          },
        },
        checkIn: dateTimeToDateOnly(now.plus({ day: 1 })),
        checkOut: dateTimeToDateOnly(now.plus({ day: 2 })),
        hotelConfirmationNumber: null,
      },
    ],
  };
};

const myTripsEmpty = {
  trips: [],
};

const createGetMyTripsHandler = (now: DateTime) => [
  http.get(`**${apiPaths.trips.getList}`, async () => {
    return HttpResponse.json(createMyTrips(now), { status: 200 });
  }),
];

const getMyTripsHandler = createGetMyTripsHandler(DateTime.now());

export const getMyTripsEmptyHandler = [
  http.get(`**${apiPaths.trips.getList}`, async () => {
    return HttpResponse.json(myTripsEmpty, { status: 200 });
  }),
];

export const tripHandlers = {
  getMyTrips: getMyTripsHandler,
  create200: createTrip200Handler,
  create409Duplicate: createTripDuplicateHandler,
  cancel200: cancelTrip200Handler,
  getTripKey200: getTripKeyHandler,
  getTripKey401: getTripKey401Handler,
  getTripKeyLoading: getTripKeyLoadingHandler,
  sendReceipt200: sendReceipt200Handler,
  sendReceipt500: sendReceipt500Handler,
  sendItinerary200: sendItinerary200Handler,
  getTripReviewLink200: createTripReviewLinkHandler(),
  getTripReviewLinkLoading: getTripReviewLinkLoadingHandler,
} as const;

export const tripHandlersFactories = {
  getMyTrips: createGetMyTripsHandler,
  getTripKey: createGetTripKeyHandler,
  cancel: createCancelTripHandler,
  getTripReviewLink: createTripReviewLinkHandler,
  createSendReceipt: createSendReceiptHandler,
  create200WithRedirect: createTrip200WithRedirectHandler,
  create422: createTripFailedHandler,
} as const;

export const confirmedTripHandlers = {
  getMyTrips: getMyTripsHandler,
  create200: createTrip200Handler,
  cancel200: cancelTrip200Handler,
  getTripKey200: getTripKeyHandler,
} as const;

export const inAttemptTripHandlers = {
  getMyTrips: getMyTripsHandler,
  create200: createTrip200Handler,
  cancel200: cancelTrip200Handler,
  getTripKey200: getTripKeyHandler,
} as const;

export const confirmedFreeCancellationTripHandlers = {
  getMyTrips: getMyTripsHandler,
  create200: createTrip200Handler,
  cancel200: cancelTrip200Handler,
  getTripKey200: getTripKeyHandler,
} as const;

export const cancelledFreeCancellationTripHandlers = {
  getMyTrips: getMyTripsHandler,
  create200: createTrip200Handler,
  cancel200: cancelTrip200Handler,
  getTripKey200: getTripKeyHandler,
} as const;

export const finishedTripHandlers = {
  getMyTrips: getMyTripsHandler,
  create200: createTrip200Handler,
  cancel200: cancelTrip200Handler,
  getTripKey200: getTripKeyHandler,
} as const;
